import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import {
  CheckSquareOutlined,
  CloseSquareOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { Button, Modal, Space, Tooltip, message } from 'antd';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import { HOME } from '../../../../constants/define/translation';
import {
  HomeWidgetsRecord,
  postDeleteHomeWidgetsRepository,
  postSortHomeWidgetsRepository,
  postUpdateHomeWidgetsRepository,
} from '../../../../repository/mytm/home/homeWidgetsRepository';
import { HomeGroupKey, HomeType, HomeWidgetStatus } from '../../../../types/home';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import { UserContext } from '../../../../repository/context/userContext';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { btnActive } from '../../../../styles';
import HomeTileRecordId from '../HomeTileRecordId';

interface ActionsColumnProps {
  record: HomeWidgetsRecord;
  onRefresh: () => void;
  onHandleClickBtnEdit?: (record: HomeWidgetsRecord) => void;
  recordsActive: HomeWidgetsRecord[];
  setLoading?: (loading: boolean) => void;
}

export default React.memo((props: ActionsColumnProps) => {
  const { t } = useTranslation(HOME);
  const { record, onRefresh, onHandleClickBtnEdit, recordsActive, setLoading } = props;
  const userContext = useContext(UserContext);

  const [deleting, setDeleting] = useState(false);
  const [activating, setActivating] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [closing, setClosing] = useState(false);

  const canActivate = record.status === HomeWidgetStatus.INACTIVE;
  const recordActive = record.status === HomeWidgetStatus.ACTIVE;
  const canNotEdit = record.status !== HomeWidgetStatus.CLOSED;
  const canClose = record.status === HomeWidgetStatus.ACTIVE && record.type === HomeType.PROMOTION;

  const handleDelete = () => {
    Modal.confirm({
      title: t('popup.title'),
      okText: t('popup.okBtn'),
      cancelText: t('popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: recordActive ? (
        <ConfirmAction
          textConfirmAction={t('popup.delete')}
          isConfirmPassword
          textConfirmPassword={t('popup.confirmPassword')}
        />
      ) : (
        t('popup.delete')
      ),
      onOk() {
        setDeleting(true);
        const updateFunc = () => {
          postDeleteHomeWidgetsRepository({ id: record.id })
            .then(r => {
              if (recordActive) {
                const deepClnRecords = [...recordsActive];
                if (record?.index) {
                  deepClnRecords.splice(Number(record?.index), 1);
                }
                postSortHomeWidgetsRepository({
                  sortOrderList: deepClnRecords.map((deepRecord, idx) => ({
                    id: deepRecord.id,
                    sortOrder: idx + 1,
                    name: deepRecord.name,
                    status: deepRecord.status,
                  })),
                })
                  .then(() => {
                    onRefresh();
                    setDeleting(false);
                  })
                  .catch(e => {
                    message.error(
                      e?.errorMessage ||
                        (record.groupKey === HomeGroupKey.HOME_TILES
                          ? t('message.updateHomeTilesError')
                          : t('message.updateHomeExplorerError')),
                    );
                    setDeleting(false);
                  });
              } else {
                setDeleting(false);
                onRefresh();
              }
            })
            .catch(e => {
              setDeleting(false);
              message.error(
                e?.errorMessage ||
                  (record.groupKey === HomeGroupKey.HOME_TILES
                    ? t('message.updateHomeTilesError')
                    : t('message.updateHomeExplorerError')),
              );
            });
        };
        if (recordActive) {
          const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
          const password = (inputPasswordNode as any).value;
          if (!userContext.user?.email || userContext.user?.email === '') {
            setDeleting(false);
            message.error(MESSAGE_ERROR.M02);
          } else {
            postLoginRepository({
              email: userContext.user?.email,
              password,
            })
              .then(() => {
                updateFunc();
              })
              .catch((e: any) => {
                setDeleting(false);
                message.error(
                  e?.errorMessage ||
                    (record.groupKey === HomeGroupKey.HOME_TILES
                      ? t('message.updateHomeTilesError')
                      : t('message.updateHomeExplorerError')),
                );
              });
          }
        } else {
          updateFunc();
        }
      },
    });
  };

  const handleActivate = () => {
    const payload = { ...record, status: HomeWidgetStatus.ACTIVE, sortOrder: recordsActive.length + 1 };
    delete payload.index;

    Modal.confirm({
      title: t('popup.title'),
      okText: t('popup.okBtn'),
      cancelText: t('popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('popup.activate')}
          isConfirmPassword
          textConfirmPassword={t('popup.confirmPassword')}
        />
      ),
      onOk() {
        setActivating(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const updateFunc = () => {
          postUpdateHomeWidgetsRepository(record.id, payload)
            .then(r => {
              setActivating(false);
              onRefresh();
            })
            .catch(e => {
              setActivating(false);
              message.error(
                e?.errorMessage ||
                  (record.groupKey === HomeGroupKey.HOME_TILES
                    ? t('message.updateHomeTilesError')
                    : t('message.updateHomeExplorerError')),
              );
            });
        };
        if (!userContext.user?.email || userContext.user?.email === '') {
          setActivating(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              setActivating(false);
              message.error(
                e?.errorMessage ||
                  (record.groupKey === HomeGroupKey.HOME_TILES
                    ? t('message.updateHomeTilesError')
                    : t('message.updateHomeExplorerError')),
              );
            });
        }
      },
    });
  };

  const handleDeactivate = () => {
    const payload = { ...record, status: HomeWidgetStatus.INACTIVE };
    delete payload.index;
    Modal.confirm({
      title: t('popup.title'),
      okText: t('popup.okBtn'),
      cancelText: t('popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('popup.deactivate')}
          isConfirmPassword
          textConfirmPassword={t('popup.confirmPassword')}
        />
      ),
      onOk() {
        setDeactivating(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const updateFunc = () => {
          postUpdateHomeWidgetsRepository(record.id, payload)
            .then(r => {
              if (record.status === HomeWidgetStatus.ACTIVE) {
                const deepClnRecords = [...recordsActive];
                if (record?.id) {
                  deepClnRecords.splice(
                    deepClnRecords.findIndex(x => x.id === record.id),
                    1,
                  );
                }
                postSortHomeWidgetsRepository({
                  sortOrderList: deepClnRecords.map((deepRecord, idx) => ({
                    id: deepRecord.id,
                    sortOrder: idx + 1,
                    name: deepRecord.name,
                    status: deepRecord.status,
                  })),
                })
                  .then(() => {
                    onRefresh();
                    setDeactivating(false);
                  })
                  .catch((e: any) => {
                    message.error(
                      e?.errorMessage ||
                        (record.groupKey === HomeGroupKey.HOME_TILES
                          ? t('message.updateHomeTilesError')
                          : t('message.updateHomeExplorerError')),
                    );
                    setDeactivating(false);
                  });
              }
            })
            .catch(e => {
              setDeactivating(false);
              message.error(
                e?.errorMessage ||
                  (record.groupKey === HomeGroupKey.HOME_TILES
                    ? t('message.updateHomeTilesError')
                    : t('message.updateHomeExplorerError')),
              );
            });
        };
        if (!userContext.user?.email || userContext.user?.email === '') {
          setDeactivating(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              setDeactivating(false);
              message.error(e?.errorMessage || t('message.updateHomeExplorerError'));
            });
        }
      },
    });
  };

  const handleClose = () => {
    const payload = { ...record, status: HomeWidgetStatus.CLOSED };
    delete payload.index;
    Modal.confirm({
      title: t('popup.title'),
      okText: t('popup.okBtn'),
      cancelText: t('popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('popup.close')}
          isConfirmPassword
          textConfirmPassword={t('popup.confirmPassword')}
        />
      ),
      onOk() {
        setClosing(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const updateFunc = () => {
          postUpdateHomeWidgetsRepository(record.id, payload)
            .then(r => {
              onRefresh();
              setClosing(false);
            })
            .catch(e => {
              setClosing(false);
              message.error(
                e?.errorMessage ||
                  (record.groupKey === HomeGroupKey.HOME_TILES
                    ? t('message.updateHomeTilesError')
                    : t('message.updateHomeExplorerError')),
              );
            });
        };
        if (!userContext.user?.email || userContext.user?.email === '') {
          setClosing(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              setClosing(false);
              message.error(
                e?.errorMessage ||
                  (record.groupKey === HomeGroupKey.HOME_TILES
                    ? t('message.updateHomeTilesError')
                    : t('message.updateHomeExplorerError')),
              );
            });
        }
      },
    });
  };

  const handleEdit = () => {
    if (recordActive) {
      Modal.confirm({
        title: t('popup.title'),
        okText: t('popup.okBtn'),
        cancelText: t('popup.cancelBtn'),
        icon: <ExclamationCircleOutlined />,
        content: recordActive ? (
          <ConfirmAction
            textConfirmAction={t('popup.edit')}
            isConfirmPassword
            textConfirmPassword={t('popup.confirmPassword')}
          />
        ) : (
          t('popup.edit')
        ),
        onOk() {
          setLoading && setLoading(true);
          if (recordActive) {
            const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
            const password = (inputPasswordNode as any).value;
            if (!userContext.user?.email || userContext.user?.email === '') {
              setLoading && setLoading(false);
              message.error(MESSAGE_ERROR.M02);
            } else {
              postLoginRepository({
                email: userContext.user?.email,
                password,
              })
                .then(() => {
                  setLoading && setLoading(false);
                  if (onHandleClickBtnEdit) {
                    onHandleClickBtnEdit(record);
                  }
                })
                .catch((e: any) => {
                  setLoading && setLoading(false);
                  message.error(
                    e?.errorMessage ||
                      (record.groupKey === HomeGroupKey.HOME_TILES
                        ? t('message.updateHomeTilesError')
                        : t('message.updateHomeExplorerError')),
                  );
                });
            }
          } else if (onHandleClickBtnEdit) {
            setLoading && setLoading(false);
            onHandleClickBtnEdit(record);
          }
        },
        onCancel() {
          // NOT
        },
      });
    } else if (onHandleClickBtnEdit) {
      onHandleClickBtnEdit(record);
    }
  };

  return (
    <StyledContainer>
      <Space size="small">
        {recordActive && (
          <Tooltip title={t('table.tooltip.deactivate')}>
            <Button
              className="deactivate"
              disabled={deactivating}
              loading={deactivating}
              icon={<PauseCircleOutlined />}
              onClick={handleDeactivate}
            />
          </Tooltip>
        )}
        {canClose && (
          <Tooltip title={t('table.tooltip.close')}>
            <Button
              className="close"
              disabled={closing}
              loading={closing}
              icon={<CloseSquareOutlined />}
              onClick={handleClose}
            />
          </Tooltip>
        )}
        {canActivate && (
          <Tooltip title={t('table.tooltip.activate')}>
            <Button
              className="active"
              disabled={activating}
              loading={activating}
              icon={<CheckSquareOutlined />}
              onClick={handleActivate}
            />
          </Tooltip>
        )}
        <HomeTileRecordId id={record.id} onlyButton />
        {canNotEdit && (
          <>
            <Tooltip title={t('table.tooltip.edit')}>
              <Button icon={<EditOutlined />} onClick={handleEdit} />
            </Tooltip>
            <Tooltip title={t('table.tooltip.delete')}>
              <Button
                className="delete"
                icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                disabled={deleting}
                loading={deleting}
                onClick={handleDelete}
              />
            </Tooltip>
          </>
        )}
      </Space>
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  ${btnActive}
`;
